import { ColDef } from "ag-grid-community";
import Enumerable from "linq";
import { SystemBalanceTypeEnum } from "../../../common/enums/SystemBalanceTypeEnum";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { IBaseGridColumnDefinition } from "../../../common/models/BaseGridColumnDefinition";
import { RowDef } from "../../../components/ag-grid-extensions/models/RowDef";
import { SideSheetGroupedSelectOption } from "../../../components/side-sheet/side-sheet-grouped-select/SideSheetGroupedSelect";
import { createMemoColumnV2, createNumericColumn, createRecentMemoColumn, createShortcutsColumn, createSparklineColumn, createSystemBalanceColumn, createTextColumn, createTimestampColumn } from "../../../components/summary-listing-grid/ColumnCreators";
import { IRoute, routes } from "../../../routes/routes";
import { AttributeSchemaTypeEnum, IAttribute } from "../../../services/attribute-data-service/Models";
import { PageConfigurationAccessEnum, PageConfigurationDataModel3, PageConfigurationModel3 } from "../../../services/page-configuration-service/Models";
import { IGroupByAttribute, IPolledDeviceSectionsModel, IPolledDevicesGridCell, IPolledDevicesGridModel, IPolledDevicesGridRow } from "../../../services/summary-listing-data-service/DeviceSummaryModels";
import { dataValueCellStyler, getDisplayText, getRoute, hasHRef, memoCellStyler, nameCellStyler } from "../common/Common";
import _ from "lodash";
import i18n from "../../../services/localization-service/i18n";
import PageConfigurationService from "../../../services/page-configuration-service/PageConfigurationService";
import { AggregateType, OptionalColumnEnum } from "../../../common/enums/DRCXEnums";
import { SummaryListingSortTypeEnum } from "../../../common/enums/SummaryListingSortTypeEnum";
import { DataTypeEnum } from "../../../common/enums/DataTypeEnum";
import { GridColumnType } from "../../../common/enums/GridColumnType";
import { AddMemoIcon, MapItIcon } from "../../action-menu/ActionMenuIcons";


export type PageConfigurationSelectOption = {
    sectionCount?: number;
} & SideSheetGroupedSelectOption;

export type GridSectionData = {
    caption: string;
    rows: RowDef[],
    cols: ColDef[]
}

const pageSize = 8;


export { pageSize }

export type DeviceRow = RowDef & {
    deviceID: number;
    deviceName: string;
    systemBalanceType: SystemBalanceTypeEnum;
    hasCommError: boolean;
    hasRecentMemo: boolean;
    memoText: string;
    memoTimestamp: string;
    memoID: number | undefined;
    memoCreatedBy: string;
    alarmStatus: number;
    functionIcons: IRoute[];
    order: number | undefined;
};

export function createViewOptions(configurations: PageConfigurationModel3[] | undefined, 
            access: PageConfigurationAccessEnum, 
            defaultID: number,
            includeCreatedBy: boolean): PageConfigurationSelectOption[] {
    return configurations?.filter(config => config.access === access)
        .map(configuration => {
            return {
                label: configuration.name as string,
                value: configuration.id as number,
                isDefault: configuration.id === defaultID,
                sectionCount: configuration.sectionCount,
                createdUserName: includeCreatedBy ? configuration.createdByUserName ?? 'Unknown' : undefined
            };
        })
        ?? new Array<PageConfigurationSelectOption>();
}

export function createGridData(summaryData: IPolledDeviceSectionsModel | undefined, 
    isPhone: boolean, 
    groupID: number,
    showShortcuts: boolean,
    sortType: SummaryListingSortTypeEnum,
    deviceViewID: UIViewEnum,
    disableNavigation: boolean,

    historicalData: boolean,
    graph: boolean,
    marketingReport: boolean,
    plungerAnalysis: boolean,
    gaugeSheet: boolean,
    dynacard: boolean,
    addMemo: boolean,
    mapIt: boolean

    
    ): GridSectionData[] {

        return summaryData?.gridData?.map(o => {
            const pinnedColumnCount: number = (o.lockedColumnCount ?? 0) + (showShortcuts ? 1 : 0) + (!isPhone ? 1 : 0) + (summaryData?.isSystemBalanceGroup ? 1 : 0);
            const cols: Array<ColDef> = createGridColumns(o, 
                                            pinnedColumnCount, 
                                            !isPhone, 
                                            showShortcuts, 
                                            sortType !== SummaryListingSortTypeEnum.Custom && (summaryData?.isSystemBalanceGroup ?? false),
                                            sortType,
                                            disableNavigation);
            const rows: Array<RowDef> = createGridRows(o, 
                                            groupID, 
                                            deviceViewID,
                                            historicalData,
                                            graph,
                                            marketingReport,
                                            plungerAnalysis,
                                            gaugeSheet,
                                            dynacard,
                                            addMemo,
                                            mapIt
                                        );
            const data = cols && rows
                ? {
                    caption: o.caption,
                    rows: rows,
                    cols: cols
                } as GridSectionData : {
                    caption: '',
                    rows: new Array<RowDef>(),
                    cols: new Array<ColDef>()
                } as GridSectionData;
            return data;
        }) ?? [{
            caption: '',
            rows: new Array<RowDef>(),
            cols: new Array<ColDef>()
        }];
}

export function createGridRows(
    //dataResponse: IPolledDevicesModel | undefined,
    gridData: IPolledDevicesGridModel | undefined,
    groupID: number,
    deviceViewID: UIViewEnum,
    historicalData: boolean,
    graph: boolean,
    marketingReport: boolean,
    plungerAnalysis: boolean,
    gaugeSheet: boolean,
    dynacard: boolean,
    addMemo: boolean,
    mapIt: boolean

    
): Array<DeviceRow> {

    return gridData?.rows.map((row: IPolledDevicesGridRow) => {
        const newRow: DeviceRow = {
            deviceID: row.deviceID,
            deviceName: row.deviceName,
            systemBalanceType: row.systemBalanceType,
            hasCommError: row.hasCommError,
            hasRecentMemo: row.hasRecentMemo,
            memoText: row.memoText,
            memoTimestamp: row.memoTimestamp,
            memoID: row.memoID,
            memoCreatedBy: row.memoCreatedBy,
            alarmStatus: row.alarmStatus,
            functionIcons: getShortcutData(row,
                    historicalData,
                    graph,
                    marketingReport,
                    plungerAnalysis,
                    gaugeSheet,
                    dynacard,
                    addMemo,
                    mapIt
            ),
            order: row.order,
            active: row.active,
            latitude: row.latitude,
            longitude: row.longitude
        };

        row.cells.forEach((cell: IPolledDevicesGridCell, index: number) => {
            const displayText = getDisplayText(cell);
            newRow[`cell${index}`] = cell;
            newRow[`cell${index}`].displayText = displayText;
            
            // convert the timestamp property from string to Date
            if(typeof (newRow[`cell${index}`].timestamp) === "string") {
                newRow[`cell${index}`].timestamp = new Date(newRow[`cell${index}`].timestamp);
            }

            if (displayText && displayText !== '' && hasHRef(cell)) {
                newRow[`cell${index}`].href = getRoute(row, cell, groupID, deviceViewID);
            }
        });

        return newRow;
    }) ?? new Array<DeviceRow>();
}

export function createGridColumns(
    //dataResponse: IPolledDevicesModel | undefined, 
    gridData: IPolledDevicesGridModel | undefined,    
    pinnedColumnCount: number, 
    isDesktop: boolean, 
    showShortcuts: boolean,
    isSystemBalance: boolean,
    sortType: SummaryListingSortTypeEnum,
    disableNavigation: boolean
    ): Array<ColDef> {
    const hasShortcuts: boolean = isDesktop;
    const hasMemos: boolean = isDesktop && !(gridData?.columns.some(o => o.optionalColumn === OptionalColumnEnum.Memos) ?? false);
    //const isSystemBalance: boolean = dataResponse?.isSystemBalanceGroup ?? false;

    const cols = new Array<ColDef>();
    let additionalCells = 0;
    //If this is a system balance group, add a column that we can group on
    if (isSystemBalance) {
        ++additionalCells;
        cols.push(createSystemBalanceColumn({ field: "systemBalanceType", headerName: "System Balance Type" }));
    }

    let cellIndex = 0;
    const otherCols = gridData?.columns
        .filter(
            col => ((hasShortcuts && showShortcuts) || col.field !== "functionIcons") &&
                (hasMemos || (col.field !== "hasRecentMemo"))
        )
        .map((col: IBaseGridColumnDefinition) => {
            if (col.field === "hasRecentMemo") {
                ++additionalCells;
                const memoColumn = createRecentMemoColumn({
                    field: col.field,
                    headerName: "Memo",
                    initialPinned: (pinnedColumnCount > 0) ? "left" : false
                });

                if (isSystemBalance && !showShortcuts) {
                    memoColumn.width = 135;
                    memoColumn.maxWidth = 135;
                }
                return memoColumn;
            } else if (col.field === "functionIcons") {
                ++additionalCells;
                return createShortcutsColumn({
                    field: col.field,
                    headerName: col.headerText,
                    initialPinned: (pinnedColumnCount > 0) ? "left" : false,
                    disableNavigation: disableNavigation
                });
            } else if (col.field.indexOf("dataValue") !== -1 && col.colType !== GridColumnType.Date) {

                const index = cellIndex++;

                if (col.dataType === DataTypeEnum.String) {

                    return createTextColumn({
                        field: `cell${index}`,
                        headerName: col.headerText,
                        styler: undefined,
                        initialSort: undefined,
                        initialPinned: ((!isDesktop) || (isDesktop && (index + additionalCells) < pinnedColumnCount)) ? "left" : false,
                        lockPinned: !isDesktop,
                        lockPosition: !isDesktop,
                        lockVisible: !isDesktop,
                        footerAggregate: col.aggregate
                    });


                } else {

                    return createNumericColumn({
                        field: `cell${index}`,
                        headerName: col.headerText,
                        styler: dataValueCellStyler,
                        initialPinned: isDesktop && ((index + additionalCells) < pinnedColumnCount) ? "left" : false,
                        footerAggregate: col.aggregate
                    });
                }
            } else if (col.field.indexOf("timestamp") !== -1 || col.colType === GridColumnType.Date) {

                const index = cellIndex++;
                return createTimestampColumn({
                    field: `cell${index}`,
                    headerName: col.headerText,
                    initialPinned: isDesktop && ((index + additionalCells) < pinnedColumnCount) ? "left" : false,
                    useDataValue: col.colType === GridColumnType.Date
                });
            } else if (col.colType === 3) {

                return createSparklineColumn({
                    field: `cell${cellIndex++}`,
                    headerName: col.headerText
                });
            } else if (col.optionalColumn === OptionalColumnEnum.Memos) {

                const index = cellIndex++;

                return  createMemoColumnV2({
                    field: `cell${index}`,
                    headerName: col.headerText,
                    styler: memoCellStyler,
                    //initialSort: sortType === SummaryListingSortTypeEnum.Alphanumeric && isNameColumn ? "asc" : undefined,
                    //initialPinned: ((!isDesktop && isNameColumn) || (isDesktop && (index + additionalCells) < pinnedColumnCount)) ? "left" : false,
                    lockPinned: !isDesktop,
                    //lockPosition: !isDesktop && isNameColumn,
                    //lockVisible: !isDesktop && isNameColumn,
                    //footerAggregate: col.aggregate,
                    //suppressAutoResize: true
                    
                });

            }  else {

                const index = cellIndex++;
                const isNameColumn: boolean = (
                    // DeviceName (for backward compatability)
                    (col.equatableKey?.startsWith("0:2:") ||
                    // DeviceDisplayName
                    col.equatableKey?.startsWith("0:3:") || // WellName
                    col.equatableKey?.startsWith("0:64:"))) ?? false;

                const coldef = createTextColumn({
                    field: `cell${index}`,
                    headerName: col.headerText,
                    //styler: col.optionalColumn === OptionalColumnEnum.Memos ? memoCellStyler : isNameColumn ? nameCellStyler : undefined,
                    styler: isNameColumn ? nameCellStyler : undefined,
                    initialSort: sortType === SummaryListingSortTypeEnum.Alphanumeric && isNameColumn ? "asc" : undefined,
                    initialPinned: ((!isDesktop && isNameColumn) || (isDesktop && (index + additionalCells) < pinnedColumnCount)) ? "left" : false,
                    lockPinned: !isDesktop,
                    lockPosition: !isDesktop && isNameColumn,
                    lockVisible: !isDesktop && isNameColumn,
                    footerAggregate: col.aggregate,
                    //suppressAutoResize: col.optionalColumn === OptionalColumnEnum.Memos ? true : undefined
                    
                });

                // if (col.optionalColumn === OptionalColumnEnum.Memos) {
                    
                //     coldef.tooltipValueGetter = (params) => params.value;
                // }

                return coldef;
            }
        }) ?? new Array<ColDef>();

    //Finally, add the rest of the columns 
    cols.push(...otherCols);
    return cols;
}

// TODO: Localize 

const addRoute = (viewID: UIViewEnum, shortcuts: IRoute[]) => {
    let route = routes.find(o => o.viewID === viewID);
    if (route) {
        if (viewID === UIViewEnum.Group_MapView) {
            route = {...route,
                name: "Map It",
                icon: MapItIcon
            };
        } else if (viewID === UIViewEnum.Device_Details_History) {
            route = {...route,
                name: "Add Memo",
                icon: AddMemoIcon
            };
        }

        shortcuts.push(route);
    }
};

export function getShortcutData(row: IPolledDevicesGridRow, 
        historicalData: boolean,
    graph: boolean,
    marketingReport: boolean,
    plungerAnalysis: boolean,
    gaugeSheet: boolean,
    dynacard: boolean,
    addMemo: boolean,
    mapIt: boolean

): IRoute[] {

    const shortcuts: IRoute[] = [];

    if (row.hasArchives && historicalData) {
        addRoute(UIViewEnum.Device_HistoricalData, shortcuts);
    }

    if (row.hasDeviceTrending && graph) {
        addRoute(UIViewEnum.Device_Graph, shortcuts);
    }

    if (row.hasMarketingReport && marketingReport) {
        addRoute(UIViewEnum.Device_MarketingReport, shortcuts);
    }

    if (row.hasPlungerLiftRunStatistics && plungerAnalysis) {
        addRoute(UIViewEnum.Device_PlungerAnalysis, shortcuts);
    }

    if (row.hasTankGaugeSheet && gaugeSheet) {
        addRoute(UIViewEnum.Device_GaugeSheet, shortcuts);
    }
    if (row.hasDynacardView && dynacard) {
        addRoute(UIViewEnum.Device_Dynacard, shortcuts);
    }

    if (row.hasMemoModify && addMemo) {
         addRoute(UIViewEnum.Device_Details_History, shortcuts)
    }

    if (row.latitude && row.longitude && mapIt) {
        addRoute(UIViewEnum.Group_MapView, shortcuts);
    }

    return shortcuts;
    //return {
        // tagThread: row.hasTagThreads
        //     ? {
        //         displayText: `${row.deviceName}: Historical Data`,
        //         href: getDeviceRoute(UIViewEnum.Device_HistoricalData, routeContext.groupID, undefined, row.deviceID)
        //     }
        //     : undefined,
        // deviceTrending: row.hasDeviceTrending
        //     ? {
        //         displayText: `${row.deviceName}: Device Graph`,
        //         href: getDeviceRoute(UIViewEnum.Device_Graph, routeContext.groupID, undefined, row.deviceID)
        //     }
        //     : undefined,
        // marketingReport: row.hasMarketingReport
        //     ? {
        //         displayText: `${row.deviceName}: Device Marketing Report`,
        //         href: getDeviceRoute(UIViewEnum.Device_MarketingReport, routeContext.groupID, undefined, row.deviceID)
        //     }
        //     : undefined,
        // plungerLiftRunStatistics: row.hasPlungerLiftRunStatistics
        //     ? {
        //         displayText: `${row.deviceName}: Plunger Analysis`,
        //         href: getDeviceRoute(UIViewEnum.Device_PlungerAnalysis, routeContext.groupID, undefined, row.deviceID)
        //     }
        //     : undefined,
        // tankGaugeSheet: row.hasTankGaugeSheet
        //     ? {
        //         displayText: `${row.deviceName}: Gauge Sheet`,
        //         href: getDeviceRoute(UIViewEnum.Device_GaugeSheet, routeContext.groupID, undefined, row.deviceID)
        //     }
        //     : undefined,
        // dynacardView: row.hasDynacardView
        //     ? {
        //         displayText: `${row.deviceName}: Dynacard View`,
        //         href: getDeviceRoute(UIViewEnum.Device_Dynacard, routeContext.groupID, undefined, row.deviceID)
        //     }
        //     : undefined
    //};
}

export function getAttributeOptions(attributes: IAttribute[] | undefined) : IAttribute[] {
    return attributes ? Enumerable.from(attributes).distinct(o => o.an).orderBy(o => o.an.toLowerCase()).toArray() : [];
}


type GroupedSection = {
    //attribute: IAttribute;
    attributeValue: string;
    attributeType: AttributeSchemaTypeEnum;
    models: IPolledDeviceSectionsModel[];

}

export type GroupedGridSection = {
    title: string;
    attributeType: AttributeSchemaTypeEnum;
    gridSectionData: GridSectionData[]
}

export function groupDataByAttribute(
    summaryData: IPolledDeviceSectionsModel | undefined,
    groupByID: number,
    groupByAttributeData: IGroupByAttribute[] | undefined,
    groupID: number,
    isPhone: boolean,
    showShortcuts: boolean,
    filterText: string,
    sortType: SummaryListingSortTypeEnum,
    deviceViewID: UIViewEnum,
    disableNavigation: boolean,
    historicalData: boolean,
    graph: boolean,
    marketingReport: boolean,
    plungerAnalysis: boolean,
    gaugeSheet: boolean,
    dynacard: boolean,
    addMemo: boolean,
    mapIt: boolean

    )
    : GroupedGridSection[] {

    if (!summaryData || !summaryData.gridData || !groupByAttributeData) {
        return [];
    }

    const selectedAttribute = summaryData?.groupByAttributes?.find(o => o.aid === groupByID);

    const groupedSections: GroupedSection[] = [];        

    const clonedModel = _.cloneDeep(summaryData);

    //console.log(`groupByAttributeData ${groupByAttributeData.length}`);
   
    groupByAttributeData.filter(o => o.value && o.value !== '').forEach(o => {

        const sectionGridData = _.cloneDeep(clonedModel.gridData);

        sectionGridData?.forEach(x => {
            // any grid rows for a device that matches the attribute object id
            const rows = Enumerable.from(x.rows)
                            .join(o.devices,
                                o => o.deviceID,
                                v => v,
                                (o) => o)
                            .toArray();

            x.rows = rows.filter(v => (v.deviceName.toLowerCase().indexOf(filterText.toLowerCase()) !== -1 || filterText.length === 0)); 
        });


        const sectionModel: IPolledDeviceSectionsModel = {
            gridData: sectionGridData
        }

        let groupedSection = groupedSections.find(v => v.attributeValue === o.value);
        if (!groupedSection) {
             groupedSection = {
                attributeValue: o.value,
                attributeType: o.attrType,
                models: [sectionModel]
            };

            groupedSections.push(groupedSection);                
        } else {


            Enumerable.from(sectionModel.gridData ?? [])
                        .join(groupedSection.models.flatMap(v => v.gridData ?? []),
                        o => o.caption,
                        v => v.caption,
                        (o, v) => v.rows = v.rows.concat(o.rows))
                        .toArray();

            // sectionModel?.gridData?.forEach(x => {

            //     const gridData = groupedSection?.models.flatMap(v => v.gridData).find(v => v?.caption === x.caption);
            //     if (gridData) {
            //         gridData.rows = gridData.rows.concat(x.rows);
            //     }
            // });
        }        
    });

    const ungroupedTitle = i18n.t('summaryListing.ungroupedTitle', {name: selectedAttribute?.an})

    // now add any not grouped attribute
    const ungroupedSection: GroupedSection = {
        attributeValue: ungroupedTitle,
        attributeType: AttributeSchemaTypeEnum.FreeformText,
        models: []
    };

    const deviceIDs = groupByAttributeData.filter(o => o.value && o.value.trim() !== '').flatMap(o => o.devices);

    clonedModel?.gridData?.forEach(o => {
        // any grid rows for a device that matches the attrribute object id
       o.rows = o.rows.filter(v => deviceIDs.find(x => x === v.deviceID) === undefined && (v.deviceName.toLowerCase().indexOf(filterText.toLowerCase()) !== -1 || filterText.length === 0)); 
    });

    ungroupedSection.models.push(clonedModel);

    groupedSections.push(ungroupedSection);    

    const returnData: GroupedGridSection[] = [];
    
    groupedSections.forEach(o => {

        const rowCount = o.models.flatMap(x => x.gridData).flatMap(x => x?.rows)?.length ?? 0;
        if (rowCount > 0) {
            returnData.push({
                title: o.attributeValue,
                attributeType: o.attributeType,
                gridSectionData: o.models.flatMap(x => createGridData(x, 
                                        isPhone, 
                                        groupID, 
                                        isPhone ? 
                                            false : 
                                            showShortcuts ?? false,
                                        sortType,
                                        deviceViewID,
                                        disableNavigation,
                                        historicalData,
                                        graph,
                                        marketingReport,
                                        plungerAnalysis,
                                        gaugeSheet,
                                        dynacard,
                                        addMemo,
                                        mapIt
                                        ))
            } as GroupedGridSection);
        }
    });

    return Enumerable.from(returnData.sort((a, b) => {
        if (a.attributeType === AttributeSchemaTypeEnum.Date) {

            const aTitle = a.title === ungroupedTitle ? new Date('1/1/2099').valueOf() :  Date.parse(a.title).valueOf();
            const bTitle = b.title === ungroupedTitle ? new Date('1/1/2099').valueOf() :  Date.parse(b.title).valueOf();
            return aTitle - bTitle;

        } else if (a.attributeType === AttributeSchemaTypeEnum.Numeric) {

            const aTitle = a.title === ungroupedTitle ? Infinity :  +a.title;
            const bTitle = b.title === ungroupedTitle ? Infinity :  +b.title;
            return aTitle - bTitle;

        } else {
            const aTitle = a.title === ungroupedTitle ? 'zzzzz' : a.title;
            const bTitle = b.title === ungroupedTitle ? 'zzzzz' : b.title;
            return (aTitle.toLowerCase()).localeCompare(bTitle.toLowerCase());
        }
    })).toArray();

}

// Logic here is for...
// User Story 49081: REACT Desktop - Summary Listing Object Grouping - UAT - Show multiple section configs as Current Unavailable when in System Balance Group
// very short sighted, no consideration regarding how to handle defaults
export function getDefaultConfigurationID(data: PageConfigurationDataModel3 | undefined, isSystemBalance: boolean): number {
    let defaultConfigID = data?.defaultPageConfigurationId ?? -1;
    
    if (isSystemBalance && defaultConfigID !== -1 && data) {

        const allConfigurations = (data.sharedConfigurations ?? []).concat(data.privateConfigurations ?? []);
        const defaultConfiguration = allConfigurations.find(o => o.id === defaultConfigID);

        if (defaultConfiguration && (defaultConfiguration.sectionCount ?? 0) > 1) {
            defaultConfigID = Enumerable.from(allConfigurations).firstOrDefault(o => (o.sectionCount ?? 0) < 2)?.id ?? -1;
        }

    }

    return defaultConfigID;
}

const _percentVariances = [1, 3, 7, 30];

export async function hasDeprecatedColumns(groupID: number, configurationID: number) {

    const configuration = await PageConfigurationService.getAccessiblePageConfiguration4(groupID, configurationID);

    if (configuration) {
        return Enumerable.from(configuration.sections)
                            .selectMany(o => o.tatColumns)
                            .any(o => (o.aggregationType === AggregateType.SingleDayAggregateInPast ||
                                (o.aggregationType === AggregateType.VariancePercentFromAggregate &&
                                    !Enumerable.from(_percentVariances).any(p => p === o.aggregationPeriod))
                            ))
    }

    return false;
}

